import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../config/supabase'
import { updateUserBudget } from '../../services/budgetService'
import { showAlert } from '../../services/dialogService'
import imageCompression from 'browser-image-compression'
import Loader from '../../components/Loader/Loader'
import './Profile.css'

function Profile() {
  const { user, loading: authLoading } = useAuth()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [avatarImageLoading, setAvatarImageLoading] = useState(false)
  const [isInitializing, setIsInitializing] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const fileInputRef = useRef(null)
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    budget: ''
  })
  const [originalFormData, setOriginalFormData] = useState({})

  useEffect(() => {
    if (!user) return

    const loadProfile = async () => {
      try {
        setLoading(true)
        setIsInitializing(true)
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single()

        if (error) throw error

        setProfile(data)
        
        // Obtener el presupuesto (compartido entre principal y partner)
        // Ambos usuarios deben tener el mismo presupuesto en su columna budget
        // Si el usuario es partner y no tiene presupuesto, obtenerlo del principal
        let budgetToShow = data.budget ? data.budget.toString() : ''
        
        // Si el usuario es partner y no tiene presupuesto, obtenerlo del principal
        if (!budgetToShow && data.partner_user_id) {
          const { data: principalProfile } = await supabase
            .from('profiles')
            .select('budget')
            .eq('id', data.partner_user_id)
            .maybeSingle()
          
          if (principalProfile?.budget) {
            budgetToShow = principalProfile.budget.toString()
            // Sincronizar el presupuesto en el partner también
            await supabase
              .from('profiles')
              .update({ budget: parseFloat(principalProfile.budget) })
              .eq('id', user.id)
          }
        }
        
        const fullName = `${data.first_name || ''} ${data.last_name || ''}`.trim()
        const formDataObj = {
          full_name: fullName,
          email: user.email || '',
          phone: data.phone || '',
          budget: budgetToShow
        }
        setFormData(formDataObj)
        setOriginalFormData(formDataObj)
        
        // Si hay avatar_url, pre-cargar la imagen antes de ocultar el loader
        if (data.avatar_url) {
          setAvatarImageLoading(true)
          // Pre-cargar la imagen en segundo plano
          const img = new Image()
          img.onload = () => {
            // Esperar suficiente tiempo para que la imagen esté completamente en caché
            // y lista para renderizar instantáneamente sin parpadeo
            setTimeout(() => {
              setAvatarImageLoading(false)
              // Dar tiempo para que el componente se renderice y la imagen se muestre
              // sin parpadeo visible
              setTimeout(() => {
                setIsInitializing(false)
                // Ahora sí ocultar loading cuando todo esté completamente listo
                setLoading(false)
              }, 400)
            }, 400)
          }
          img.onerror = () => {
            console.error('Error pre-cargando imagen de avatar:', data.avatar_url)
            setAvatarImageLoading(false)
            setTimeout(() => {
              setIsInitializing(false)
              setLoading(false)
            }, 150)
          }
          // No agregar timestamp aquí para que use la caché del navegador
          img.src = data.avatar_url
        } else {
          setAvatarImageLoading(false)
          // Esperar un frame adicional para evitar parpadeo
          setTimeout(() => {
            setIsInitializing(false)
            setLoading(false)
          }, 150)
        }
      } catch (error) {
        console.error('Error loading profile:', error)
        setAvatarImageLoading(false)
        setIsInitializing(false)
        setLoading(false)
      }
    }

    loadProfile()
  }, [user])

  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !user) return

    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      await showAlert('Please select a valid image file.')
      return
    }

    // Validar tamaño (máximo 10MB antes de comprimir)
    if (file.size > 10 * 1024 * 1024) {
      await showAlert('The image is too large. Please select an image smaller than 10MB.')
      return
    }

    try {
      setUploadingAvatar(true)

      // Comprimir la imagen antes de subirla
      let compressedFile = file
      
      try {
        const options = {
          maxSizeMB: 0.5, // Máximo 500KB después de comprimir
          maxWidthOrHeight: 800, // Redimensionar a máximo 800px
          useWebWorker: true,
          fileType: file.type,
          initialQuality: 0.8
        }

        const compressed = await imageCompression(file, options)
        
        // Verificar que el archivo comprimido sea válido
        if (compressed && compressed.size > 0) {
          compressedFile = compressed
        } else {
          console.warn('Archivo comprimido inválido, usando original')
          compressedFile = file
        }
      } catch (compressionError) {
        console.warn('Error comprimiendo imagen, usando original:', compressionError)
        compressedFile = file
      }

      // Eliminar el avatar anterior si existe (antes de subir el nuevo)
      if (profile?.avatar_url) {
        try {
          // Extraer el path del archivo desde la URL
          // La URL de Supabase Storage tiene formato: https://[project].supabase.co/storage/v1/object/public/avatars/[filename]
          // o: https://[project].supabase.co/storage/v1/object/public/avatars/[folder]/[filename]
          const urlParts = profile.avatar_url.split('/')
          const pathIndex = urlParts.indexOf('avatars')
          
          if (pathIndex !== -1 && pathIndex < urlParts.length - 1) {
            // Construir el path completo después de 'avatars' en la URL
            // Esto incluye cualquier carpeta y el nombre del archivo
            const pathAfterAvatars = urlParts.slice(pathIndex + 1).join('/')
            
            // Intentar eliminar con el path completo
            const { error: removeError } = await supabase.storage
              .from('avatars')
              .remove([pathAfterAvatars])
            
            if (removeError) {
              console.warn('Error eliminando avatar anterior con path completo, intentando solo nombre:', removeError)
              // Intentar con solo el nombre del archivo (último elemento de la URL)
              const fileName = urlParts[urlParts.length - 1]
              if (fileName && fileName.includes('.')) {
                const { error: removeError2 } = await supabase.storage
                  .from('avatars')
                  .remove([fileName])
                
                if (removeError2) {
                  console.warn('Error eliminando avatar anterior con solo nombre:', removeError2)
                }
              }
            }
          }
        } catch (deleteError) {
          console.warn('Error eliminando avatar anterior:', deleteError)
          // Continuar aunque falle la eliminación
        }
      }

      // Subir imagen comprimida a Supabase Storage
      // Usar siempre .jpg para el nombre del archivo para evitar problemas de tipo
      const fileExt = 'jpg'
      const fileName = `avatar-${user.id}-${Date.now()}.${fileExt}`
      // Subir directamente en la raíz del bucket 'avatars', sin crear subcarpetas
      // El path debe ser solo el nombre del archivo cuando usas .from('avatars')
      const filePath = fileName

      // Asegurar que el tipo de contenido sea correcto
      const contentType = compressedFile.type || 'image/jpeg'

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, compressedFile, { 
          upsert: true,
          cacheControl: '3600',
          contentType: contentType
        })

      if (uploadError) {
        console.error('Upload error details:', uploadError)
        throw new Error(uploadError.message || 'Error uploading image. Verify that the "avatars" bucket exists and is configured as public.')
      }

      // Obtener URL pública - usar el mismo path (solo el nombre del archivo)
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath)
      
      // Verificar que la URL tenga el formato correcto (debe ser la URL pública, NO del dashboard)
      if (!publicUrl) {
        console.error('URL pública es nula o indefinida')
        throw new Error('Could not generate public URL. Verify that the "avatars" bucket exists and is public.')
      }
      
      // La URL pública debe ser: https://[project].supabase.co/storage/v1/object/public/avatars/[filename]
      // NO debe ser: https://supabase.com/dashboard/... (esa es la URL del dashboard, no del archivo)
      if (publicUrl.includes('supabase.com/dashboard')) {
        console.error('ERROR: Se está generando URL del dashboard en lugar de URL pública:', publicUrl)
        throw new Error('Generated URL is from the dashboard, not public. Verify that the "avatars" bucket is public and getPublicUrl() works correctly.')
      }
      
      if (!publicUrl.includes('/storage/v1/object/public/avatars/')) {
        console.error('URL pública no tiene el formato esperado:', publicUrl)
        throw new Error('Generated public URL is not valid. Verify that the "avatars" bucket is public.')
      }

      // Actualizar perfil con la URL del avatar (sin timestamp, solo la URL base)
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id)

      if (updateError) {
        console.error('Update error details:', updateError)
        throw updateError
      }

      // Recargar el perfil para asegurar que se actualice
      const { data: updatedProfile, error: reloadError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      if (reloadError) {
        console.warn('Error reloading profile:', reloadError)
        // Actualizar manualmente si la recarga falla
        setProfile({ ...profile, avatar_url: publicUrl })
      } else {
        setProfile(updatedProfile)
      }

      // Limpiar el input para permitir seleccionar la misma imagen de nuevo
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      // Refrescar la página para mostrar la imagen actualizada
      setTimeout(() => {
        window.location.reload()
      }, 500)
    } catch (error) {
      console.error('Error uploading avatar:', error)
        const errorMessage = error.message || 'Error uploading profile picture. Please try again.'
        await showAlert(errorMessage)
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!user || saving) return

    try {
      setSaving(true)
      
      // Separar nombre completo en first_name y last_name
      const nameParts = formData.full_name.trim().split(' ')
      const firstName = nameParts[0] || ''
      const lastName = nameParts.slice(1).join(' ') || ''

      // Actualizar perfil
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
          phone: formData.phone || null,
        })
        .eq('id', user.id)

      if (profileError) throw profileError

      // Actualizar presupuesto
      if (formData.budget) {
        await updateUserBudget(user.id, parseFloat(formData.budget))
      }

      setOriginalFormData(formData)
      
      // Show success message
      await showAlert('Profile updated successfully')
      
      // Refrescar la página para mostrar los cambios al instante
      setTimeout(() => {
        window.location.reload()
      }, 300)
    } catch (error) {
      console.error('Error updating profile:', error)
      await showAlert('Error updating profile. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setFormData(originalFormData)
  }

  const handleDeleteAccount = async () => {
    if (!user || deleting) return

    try {
      setDeleting(true)

      // Paso 1: Verificar si existe un partner
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('partner_user_id')
        .eq('id', user.id)
        .single()

      // Buscar si hay un perfil partner que apunte a este usuario
      const { data: partnerProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('partner_user_id', user.id)
        .maybeSingle()

      const hasPartner = currentProfile?.partner_user_id || partnerProfile?.id
      const isPartner = !!currentProfile?.partner_user_id
      const principalUserId = isPartner ? currentProfile.partner_user_id : user.id

      // Paso 2: Eliminar imágenes RSVP
      // IMPORTANTE: Eliminamos las imágenes RSVP siempre, tanto si es principal como partner
      // porque las imágenes están asociadas al usuario que las subió
      try {
        console.log('🗑️ Eliminando imágenes RSVP del usuario:', user.id)
        
        // Obtener todas las bodas del usuario (o del principal si es partner)
        // Si es partner, las bodas están asociadas al principal, pero las imágenes pueden estar del partner
        const { data: weddings, error: weddingsError } = await supabase
          .from('weddings')
          .select('id')
          .eq('user_id', principalUserId)

        if (!weddingsError && weddings && weddings.length > 0) {
          const weddingIds = weddings.map(w => w.id)
          console.log('📋 Bodas encontradas:', weddingIds.length)

          // Obtener todas las invitaciones RSVP con imágenes de estas bodas
          const { data: rsvpInvites, error: rsvpError } = await supabase
            .from('rsvp_invites')
            .select('image_url, wedding_id')
            .in('wedding_id', weddingIds)
            .not('image_url', 'is', null)

          if (!rsvpError && rsvpInvites && rsvpInvites.length > 0) {
            console.log('🖼️ Invitaciones RSVP con imágenes encontradas:', rsvpInvites.length)
            
            // Eliminar cada imagen RSVP del storage
            const imagesToDelete = []
            for (const invite of rsvpInvites) {
              if (invite.image_url) {
                try {
                  // Extraer el path del archivo desde la URL
                  // La URL es algo como: https://xxxxx.supabase.co/storage/v1/object/public/rsvp_assets/user-id/filename.jpg
                  const urlParts = invite.image_url.split('/rsvp_assets/')
                  if (urlParts.length > 1) {
                    const filePath = urlParts[1]
                    imagesToDelete.push(filePath)
                    console.log('📝 Imagen RSVP a eliminar:', filePath)
                  } else {
                    console.warn('⚠️ No se pudo extraer el path de la URL:', invite.image_url)
                  }
                } catch (imgError) {
                  console.warn('⚠️ Error procesando imagen RSVP:', imgError)
                }
              }
            }
            
            // Eliminar todas las imágenes de una vez
            if (imagesToDelete.length > 0) {
              const { error: deleteError } = await supabase.storage
                .from('rsvp_assets')
                .remove(imagesToDelete)

              if (deleteError) {
                console.error('❌ Error eliminando imágenes RSVP:', deleteError)
                // Intentar eliminar una por una si falla el batch
                for (const filePath of imagesToDelete) {
                  try {
                    const { error: singleDeleteError } = await supabase.storage
                      .from('rsvp_assets')
                      .remove([filePath])
                    if (singleDeleteError) {
                      console.warn('⚠️ Error eliminando imagen individual:', filePath, singleDeleteError)
                    } else {
                      console.log('✅ Imagen RSVP eliminada:', filePath)
                    }
                  } catch (e) {
                    console.warn('⚠️ Excepción eliminando imagen:', filePath, e)
                  }
                }
              } else {
                console.log(`✅ ${imagesToDelete.length} imágenes RSVP eliminadas exitosamente`)
              }
            }
          } else {
            console.log('ℹ️ No se encontraron invitaciones RSVP con imágenes')
          }
        } else {
          console.log('ℹ️ No se encontraron bodas para este usuario')
        }
        
        // ADICIONAL: Eliminar TODAS las imágenes del usuario del bucket rsvp_assets
        // Esto asegura que se eliminen imágenes huérfanas que puedan existir
        try {
          console.log('🔍 Buscando imágenes huérfanas en rsvp_assets para el usuario:', user.id)
          const { data: allFiles, error: listError } = await supabase.storage
            .from('rsvp_assets')
            .list(user.id, {
              limit: 1000,
              offset: 0,
              sortBy: { column: 'created_at', order: 'desc' }
            })
          
          if (!listError && allFiles && allFiles.length > 0) {
            console.log(`📁 Encontradas ${allFiles.length} imágenes en la carpeta del usuario`)
            const orphanFiles = allFiles.map(file => `${user.id}/${file.name}`)
            
            const { error: orphanDeleteError } = await supabase.storage
              .from('rsvp_assets')
              .remove(orphanFiles)
            
            if (orphanDeleteError) {
              console.warn('⚠️ Error eliminando imágenes huérfanas:', orphanDeleteError)
            } else {
              console.log(`✅ ${orphanFiles.length} imágenes huérfanas eliminadas`)
            }
          } else if (listError) {
            console.warn('⚠️ Error listando archivos del usuario:', listError)
          }
        } catch (orphanError) {
          console.warn('⚠️ Error buscando imágenes huérfanas:', orphanError)
        }
      } catch (rsvpStorageError) {
        console.error('❌ Error eliminando imágenes RSVP del storage:', rsvpStorageError)
        // Continuar con la eliminación aunque falle el storage
      }

      // Paso 3: Eliminar avatar del storage si existe
      if (profile?.avatar_url) {
        try {
          // Extraer el path del archivo desde la URL
          // La URL de Supabase Storage tiene formato: https://[project].supabase.co/storage/v1/object/public/avatars/[filename]
          const urlParts = profile.avatar_url.split('/')
          const pathIndex = urlParts.indexOf('avatars')
          
          if (pathIndex !== -1 && pathIndex < urlParts.length - 1) {
            // Construir el path completo después de 'avatars' en la URL
            const pathAfterAvatars = urlParts.slice(pathIndex + 1).join('/')
            
            // Intentar eliminar con el path completo
            const { error: removeError } = await supabase.storage
              .from('avatars')
              .remove([pathAfterAvatars])
            
            if (removeError) {
              console.warn('Error eliminando avatar con path completo, intentando solo nombre:', removeError)
              // Intentar con solo el nombre del archivo (último elemento de la URL)
              const fileName = urlParts[urlParts.length - 1]
              if (fileName && fileName.includes('.')) {
                const { error: removeError2 } = await supabase.storage
                  .from('avatars')
                  .remove([fileName])
                
                if (removeError2) {
                  console.warn('Error eliminando avatar con solo nombre:', removeError2)
                  // Intentar eliminar todos los avatares que empiecen con avatar-{user.id}-
                  const avatarPattern = `avatar-${user.id}-%`
                  // Listar todos los archivos del usuario y eliminarlos
                  const { data: files, error: listError } = await supabase.storage
                    .from('avatars')
                    .list('', {
                      search: `avatar-${user.id}-`
                    })
                  
                  if (!listError && files && files.length > 0) {
                    const fileNames = files.map(f => f.name)
                    await supabase.storage
                      .from('avatars')
                      .remove(fileNames)
                  }
                }
              }
            }
          } else {
            // Si no encontramos 'avatars' en la URL, intentar con solo el nombre del archivo
            const fileName = urlParts[urlParts.length - 1]
            if (fileName && fileName.includes('.')) {
              await supabase.storage
                .from('avatars')
                .remove([fileName])
            }
          }
        } catch (storageError) {
          console.warn('Error deleting avatar from storage:', storageError)
          // Continuar con la eliminación aunque falle el storage
        }
      }

      // Paso 4: Intentar eliminar cuenta de autenticación usando función SQL
      // NOTA: Si falla, la Edge Function eliminará el perfil primero y luego auth.users
      let authUserDeleted = false
      let authUserError = null
      
      try {
        console.log('🔐 Llamando a función SQL delete_user_from_auth para eliminar usuario de auth.users:', user.id)
        
        // Llamar a la función SQL que elimina de auth.users
        const { data: deleteResult, error: deleteError } = await supabase.rpc('delete_user_from_auth', {
          user_uuid: user.id
        })
        
        console.log('📊 Resultado de delete_user_from_auth:', {
          data: deleteResult,
          error: deleteError,
          dataType: typeof deleteResult,
          dataString: String(deleteResult)
        })
        
        if (deleteError) {
          console.error('❌ Error en función SQL delete_user_from_auth:', deleteError)
          authUserError = `Error deleting user from auth.users: ${deleteError.message || deleteError.code || 'Unknown error'}`
          console.error('❌ Error:', authUserError)
        } else {
          // PostgreSQL puede retornar boolean como true, 't', 1, etc.
          // Verificar múltiples formatos posibles
          const isSuccess = deleteResult === true || 
                           deleteResult === 't' || 
                           deleteResult === 1 || 
                           deleteResult === 'true' ||
                           String(deleteResult).toLowerCase() === 'true'
          
          console.log('🔍 Validación de resultado:', {
            deleteResult,
            isSuccess,
            checks: {
              '=== true': deleteResult === true,
              "=== 't'": deleteResult === 't',
              '=== 1': deleteResult === 1,
              "=== 'true'": deleteResult === 'true',
              'toLowerCase === true': String(deleteResult).toLowerCase() === 'true'
            }
          })
          
          if (isSuccess) {
            console.log('✅ Usuario eliminado de auth.users exitosamente')
            authUserDeleted = true
          } else {
            console.warn('⚠️ Función SQL retornó false. Supabase está bloqueando el DELETE en auth.users.')
            console.warn('⚠️ Intentando método alternativo usando Edge Function...')
            
            // Método alternativo: Usar Edge Function que tiene permisos de administrador
            try {
              const { data: sessionData } = await supabase.auth.getSession()
              const accessToken = sessionData?.session?.access_token
              
              if (!accessToken) {
                throw new Error('No active session')
              }

              // Obtener la URL de Supabase desde las variables de entorno
              const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
              
              if (!supabaseUrl) {
                throw new Error('Supabase URL not found. Verify that VITE_SUPABASE_URL is set in .env')
              }

              // Llamar a la Edge Function
              const response = await fetch(`${supabaseUrl}/functions/v1/delete-user-auth`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ user_uuid: user.id })
              })
              
              const result = await response.json()
              
              if (response.ok && result.success) {
                console.log('✅ Usuario eliminado usando Edge Function')
                authUserDeleted = true
              } else {
                console.error('❌ Error en Edge Function:', result)
                authUserError = result.error || `Edge Function failed. Please delete the user manually by running: DELETE FROM auth.users WHERE id = '${user.id}'; in Supabase SQL Editor.`
              }
            } catch (altError) {
              console.error('❌ Error llamando Edge Function:', altError)
              authUserError = `Error calling Edge Function: ${altError.message}. Please delete the user manually by running: DELETE FROM auth.users WHERE id = '${user.id}'; in Supabase SQL Editor.`
            }
          }
        }
      } catch (sqlError) {
        console.error('❌ Error llamando a función SQL delete_user_from_auth:', sqlError)
        console.error('Tipo:', typeof sqlError)
        console.error('Mensaje:', sqlError.message)
        
        // Si la función no existe, mostrar instrucciones
        if (sqlError.message?.includes('function') && sqlError.message?.includes('does not exist')) {
          authUserError = `SQL function 'delete_user_from_auth' does not exist. Please run the file 3_SISTEMA_ELIMINACION_CONDICIONAL_PERFILES.sql in Supabase SQL Editor to create the function.`
        } else {
          authUserError = sqlError.message || 'Unknown error calling SQL function'
        }
      }

      // Paso 5: Eliminar perfil (solo si auth.users no fue eliminado por Edge Function)
      // Si la Edge Function eliminó el usuario, el perfil ya fue eliminado por la Edge Function
      // El trigger SQL se encargará automáticamente de:
      // - Si NO hay partner → eliminar bodas y todo relacionado
      // - Si SÍ hay partner → solo eliminar el perfil individual
      if (!authUserDeleted) {
        const { error: deleteProfileError } = await supabase
          .from('profiles')
          .delete()
          .eq('id', user.id)

        if (deleteProfileError) {
          // Si el error es que el perfil no existe, está bien (puede haber sido eliminado por CASCADE)
          if (deleteProfileError.code === 'PGRST116' || deleteProfileError.message?.includes('No rows')) {
            console.log('Profile already deleted (possibly by CASCADE)')
          } else {
            console.error('Error deleting profile:', deleteProfileError)
            throw new Error(`Database error deleting user: ${deleteProfileError.message || deleteProfileError.code || 'Unknown error'}`)
          }
        }
      } else {
        console.log('Profile already deleted by Edge Function')
      }
      
      // Cerrar sesión solo si el usuario todavía existe
      // Si el usuario fue eliminado de auth.users, el signOut fallará con 403
      // Eso es normal y esperado, así que lo manejamos silenciosamente
      if (authUserDeleted) {
        // El usuario ya fue eliminado, no intentar signOut (fallará con 403)
        console.log('Usuario eliminado de auth.users, saltando signOut')
      } else {
        // Intentar cerrar sesión solo si el usuario todavía existe
        try {
          await supabase.auth.signOut()
        } catch (signOutError) {
          // Si falla con 403, es porque el usuario ya fue eliminado (normal)
          if (signOutError?.status === 403 || signOutError?.message?.includes('403')) {
            console.log('signOut falló con 403 (usuario ya eliminado, esto es normal)')
          } else {
            console.warn('Error al cerrar sesión:', signOutError)
          }
        }
      }
      
      // Mostrar mensaje final según el resultado
      if (authUserDeleted) {
        if (hasPartner) {
          await showAlert('Your profile has been successfully deleted.\n\nYour partner will continue to have access to your shared wedding planning information, including guests, tasks, and all other details.')
        } else {
          await showAlert('Your account has been successfully deleted.\n\nWe\'re sorry to see you go! All your wedding planning data has been removed from our system.')
        }
      } else {
        // Si falló la eliminación de auth.users, mostrar mensaje amigable
        if (hasPartner) {
          await showAlert('Your profile has been removed from the wedding planning system.\n\nYour partner will continue to have access to your shared wedding information.\n\nIf you experience any issues, please contact support.')
        } else {
          await showAlert('Your account has been removed from our wedding planning system.\n\nWe\'re sorry to see you go! If you experience any issues, please contact support.')
        }
      }
      
      // Paso 5: Limpiar sesión local antes de redirigir para evitar 403
      // Limpiar todos los datos de autenticación de Supabase del localStorage
      try {
        // Limpiar todas las claves relacionadas con Supabase
        const keysToRemove = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && (key.includes('supabase') || key.includes('sb-') || key === 'token')) {
            keysToRemove.push(key)
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key))
        sessionStorage.clear()
      } catch (e) {
        // Ignorar errores al limpiar storage
        console.log('Error limpiando storage:', e)
      }
      
      // Redirigir a la página principal
      // El error 403 que puede aparecer es normal: el usuario ya no existe
      window.location.href = '/'
    } catch (error) {
      console.error('Error deleting account:', error)
      await showAlert('We encountered an issue while deleting your account.\n\nPlease try again later or contact our support team for assistance.')
      setDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const getInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase()
    }
    if (user?.email) {
      return user.email[0].toUpperCase()
    }
    return 'U'
  }

  const formatBudget = (budget) => {
    if (!budget) return '0'
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(parseFloat(budget))
  }

  // Calcular si debería mostrarse el loader (solo mostrar si TODOS los estados indican carga)
  const isLoading = authLoading || loading || avatarImageLoading || isInitializing
  
  // Mostrar loader mientras se cargan los datos, mientras se verifica la autenticación,
  // mientras se carga la imagen del avatar (si existe), o durante la inicialización
  if (isLoading) {
    return <Loader />
  }

  const fullName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'User'
  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData)

  return (
    <div className="profile-page">
      <div className="profile-container">
        {/* Sección de Información del Usuario */}
        <div className="profile-user-info">
          <div className="profile-avatar-container">
            <div className="profile-avatar" onClick={handleAvatarClick} style={{ cursor: uploadingAvatar ? 'wait' : 'pointer' }}>
              {uploadingAvatar ? (
                <div className="avatar-uploading">
                  <span>Loading...</span>
                </div>
              ) : profile?.avatar_url ? (
                <>
                  <img 
                    src={profile.avatar_url}
                    alt={fullName}
                    onError={(e) => {
                      // Si la imagen falla al cargar, mostrar las iniciales
                      console.error('Error cargando imagen de avatar:', profile.avatar_url)
                      console.error('URL intentada:', `${profile.avatar_url}?t=${Date.now()}`)
                      const imgElement = e.target
                      const initialElement = imgElement.parentElement?.querySelector('.avatar-initial')
                      if (imgElement) imgElement.style.display = 'none'
                      if (initialElement) initialElement.style.display = 'flex'
                    }}
                    onLoad={() => {
                      const initialElement = document.querySelector('.profile-avatar .avatar-initial')
                      if (initialElement) initialElement.style.display = 'none'
                    }}
                    style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <span className="avatar-initial" style={{ display: 'none' }}>{getInitials()}</span>
                </>
              ) : (
                <span className="avatar-initial" style={{ display: 'flex' }}>{getInitials()}</span>
              )}
              <div className="avatar-camera-icon" style={{ display: uploadingAvatar ? 'none' : 'flex' }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 11.5C10.3807 11.5 11.5 10.3807 11.5 9C11.5 7.61929 10.3807 6.5 9 6.5C7.61929 6.5 6.5 7.61929 6.5 9C6.5 10.3807 7.61929 11.5 9 11.5Z" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 13.5V4.5C3 3.67157 3.67157 3 4.5 3H6.22727C6.70461 3 7.16186 2.81039 7.5 2.47227L8.22727 1.745C8.56539 1.40689 9.02264 1.21727 9.5 1.21727H12.5C13.3284 1.21727 14 1.88885 14 2.71727V13.5C14 14.3284 13.3284 15 12.5 15H4.5C3.67157 15 3 14.3284 3 13.5Z" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <p className="avatar-change-text">Click to change</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              style={{ display: 'none' }}
            />
          </div>

          <div className="profile-user-details">
            <h1 className="profile-user-name">{fullName}</h1>
            <p className="profile-user-email">{user?.email || ''}</p>
            <p className="profile-user-plan">Free Plan</p>
            <p className="profile-user-budget">
              Total Budget: <span className="budget-amount">{formatBudget(formData.budget || profile?.budget)}</span>
            </p>
          </div>
        </div>

        {/* Edit Form */}
        <div className="profile-edit-section">
          <h2 className="profile-edit-title">Edit Information</h2>
          
          <form onSubmit={handleSubmit} className="profile-form">
            <div className="profile-form-group">
              <label htmlFor="full_name">Full Name</label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
              />
            </div>

            <div className="profile-form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled
              />
            </div>

            <div className="profile-form-group">
              <label htmlFor="phone">Phone (Optional)</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+1 234 567 890"
              />
            </div>

            <div className="profile-form-group">
              <label htmlFor="budget">Total Wedding Budget</label>
              <input
                type="number"
                id="budget"
                name="budget"
                step="1"
                value={formData.budget}
                onChange={handleInputChange}
                placeholder="500000"
              />
            </div>

            <div className="profile-form-actions">
              <button type="submit" className="profile-save-button" disabled={saving || !hasChanges}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button type="button" className="profile-cancel-button" onClick={handleCancel} disabled={!hasChanges}>
                Cancel
              </button>
            </div>
          </form>
        </div>

        {/* Danger Zone */}
        <div className="profile-danger-zone">
          <h2 className="danger-zone-title">Danger Zone</h2>
          <p className="danger-zone-warning">
            Once you delete your account, you won't be able to recover it. The following will be permanently deleted:
          </p>
          <ul className="danger-zone-list">
            <li>Your authentication account (email, password, authorization to sign in)</li>
            <li>Your profile (name, personal information, avatar)</li>
            <li>All your weddings and associated information</li>
            <li>All guests and related data</li>
          </ul>
          <div className="danger-zone-final-warning">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 6V9M9 11.5H9.01M4 15.5H14C15.1046 15.5 16 14.6046 16 13.5V4.5C16 3.39543 15.1046 2.5 14 2.5H4C2.89543 2.5 2 3.39543 2 4.5V13.5C2 14.6046 2.89543 15.5 4 15.5Z" stroke="#d32f2f" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>This action is permanent and irreversible.</span>
          </div>
          <button 
            className="danger-zone-delete-button" 
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleting}
          >
            Delete My Account
          </button>
        </div>

        {/* Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="delete-confirm-modal">
            <div className="delete-confirm-content">
              <h3>Are you sure?</h3>
              <p>This action cannot be undone. Your account and all your data will be permanently deleted.</p>
              <div className="delete-confirm-actions">
                <button 
                  className="delete-confirm-button" 
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Yes, delete account'}
                </button>
                <button 
                  className="delete-cancel-button" 
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Profile
